import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, first, map, Observable, Subject, take, tap } from 'rxjs';
import { IEmployee } from 'src/app/employee/employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { IAssignment, ICreateAssignmentRequest, IUpdateAssignmentRequest, TaskStatues } from '../../models/assignment';
import { IAssignmentType } from '../../models/assignment-type';
import { AssignmentStorageService } from '../../storage/assignment-storage.service';
import {Location} from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { translate } from '@jsverse/transloco';
import { ObjectIdIsNotNullValidator } from 'src/app/shared/validators/objectIdIsNotNull.validator';
import { AssignmentService } from '../../services/assignment.service';
import { AssignmentTypeService } from '../../services/assignment-type.service';
import { AssignmentInternalNumberValidator } from 'src/app/shared/validators-async/assignmentInternalNumber.validator';
import { IContact } from 'src/app/contact/models/contact';
import { PasteCleanupSettings } from '@progress/kendo-angular-editor';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { dueDateClass } from 'src/app/shared/helpers/date-helpers';
import { ListQueryStateService } from 'src/app/shared/services/list-query-state.service';
@UntilDestroy()
@Component({
    selector: 'app-assignment-detail',
    templateUrl: './assignment-detail.component.html',
    styleUrls: ['./assignment-detail.component.css'],
    standalone: false
})
export class AssignmentDetailComponent implements OnInit {

  constructor(
    private assignmentStore : AssignmentStorageService,
    private employeeService: EmployeeService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private appnotificationService: AppNotificationService,
    private assignmentService: AssignmentService,
    private assignmentTypeService: AssignmentTypeService,
    private permissionStore: PermissionStorageService,
    private listQueryState: ListQueryStateService
  ) { }

  public assignment$: Observable<IAssignment | null> =
    this.assignmentStore.assignment$;

  canEdit = false;
  public deletionDialogOpened = false;

  employees: IEmployee[];

  currentEmployee : IEmployee;

  form: UntypedFormGroup;

  dueDateClass = dueDateClass;

  defaultItem = {
    id: null,
    name: 'Bitte wählen',
    firstName: 'Bitte wählen',
    lastName: null
  }

  pasteCleanupSettings: PasteCleanupSettings = {
    stripTags: ['img'],           // Remove all <img> tags
    removeMsClasses: true,
    removeMsStyles: true
  };

  taskStatusArray = enumToArray(TaskStatues, "assignment.taskStatuses.");

  assignmentTypes: IAssignmentType[] = [];

  ngOnInit(): void {
    this.currentEmployee = this.route.snapshot.data['currentEmployee'] as IEmployee
    this.canEdit = this.hasPermission('Permissions.Assignments.Update');
    this.initForm();
    this.initFromUpdates();
    this.employeeService.listEmployees({pageNumber:0, pageSize:999999})
      .pipe(untilDestroyed(this))
      .subscribe(employees => this.employees = employees.data)

    this.assignmentTypeService.searchAssignmentTypes({ pageNumber: 0, pageSize: 999999 })
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => {
        this.assignmentTypes = response.data.filter(t => t.isActive);
      });

    if (!this.canEdit) {
      this.form.disable();
    }
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }

  initForm(){
      this.form = new UntypedFormGroup({
        id: new UntypedFormControl(null),
        title: new UntypedFormControl(null, Validators.required),
        internalNumber: new UntypedFormControl(null,{asyncValidators: AssignmentInternalNumberValidator(this.assignmentService), updateOn:"blur"}),
        description: new UntypedFormControl(null ),
        specialNote: new UntypedFormControl(null),
        dueDate: new UntypedFormControl(null),
        client: new UntypedFormControl(null, [Validators.required, ObjectIdIsNotNullValidator()]),
        executionLocation: new UntypedFormControl(null),
        owner: new UntypedFormControl(this.currentEmployee ?? null, [Validators.required, ObjectIdIsNotNullValidator()]),
        executorIds: new UntypedFormControl([]),
        typeId: new UntypedFormControl(null, Validators.required),
        taskStatus: new UntypedFormControl(10, Validators.required)
      })
  }

  initFromUpdates(){
    this.assignment$
    .pipe(
      untilDestroyed(this)
    )
    .subscribe(assignment => {
      if(assignment){
        this.form.patchValue({
          id: assignment.id ?? null,
          title: assignment.title,
          internalNumber: assignment.internalNumber ?? null,
          description: assignment.description ?? null,
          specialNote: assignment.specialNote ?? null,
          dueDate: assignment.dueDate ? new Date(assignment.dueDate) : null,
          client: assignment.client,
          executionLocation: assignment.executionLocation ?? null,
          owner: assignment.owner,
          executorIds: assignment.executors?.map(e => e.id) ?? [],
          typeId: assignment.typeId ?? assignment.type?.id ?? null,
          taskStatus: assignment.taskStatus ?? 10
        })
      }

    })
  }

  onSubmit(){
    const formValues = this.form.value;

    if(formValues.id){
      const request : IUpdateAssignmentRequest = formValues;
      request.clientId = formValues.client.id;
      request.ownerId = formValues.owner.id;
      request.executionLocationId = formValues.executionLocation?.id ?? null;
      request.executorIds = formValues.executorIds ?? [];
      delete request['client'];
      delete request['owner'];
      delete request['executionLocation'];
      this.assignmentStore.updateAssignment(request);
      this.form.markAsPristine();
    } else {
      const request : ICreateAssignmentRequest = formValues;
      request.clientId = formValues.client.id;
      request.ownerId = formValues.owner.id;
      request.executionLocationId = formValues.executionLocation?.id ?? null;
      request.executorIds = formValues.executorIds ?? [];
      delete request['client'];
      delete request['owner'];
      delete request['executionLocation'];
      this.assignmentStore.createAssignment(request);
      this.form.markAsPristine();
      const id = this.assignment$
        .pipe(
          filter(assignment => assignment?.id != null),
          map(assignment => assignment.id),
          take(1),
          untilDestroyed(this)
        )
        .subscribe(assignmentId => {
          this.router.navigate(['/assignment',assignmentId])
        });
      
    }
    
  }

  delete(assignmentId){
    if (!assignmentId) return;

    this.assignmentStore.deleteAssignment(assignmentId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.router.navigate(['/assignments'], {
          queryParams: this.listQueryState.getQueryParams('assignments')
        }),
        error: () => {
          this.appnotificationService.notification$.next({
            message: translate('assignment.deleteError'),
            style: 'error'
          });
        }
      });
  }

  openContact(controlName: string = 'client'){
    const contact = this.form.get(controlName).value;
    if (contact == null) return;
    this.router.navigate(['contact', contact.id])
  }

  openGoogleMaps(controlName: string = 'client'){
    const contact = this.form.get(controlName).value;

    if(this.contactHasValidAddress(contact)){
      const url = `https://www.google.com/maps/place/${contact.street}+${contact.zip}+${contact.city}`
      window.open(url, '_blank');
    }

  }

  contactHasValidAddress(contact? : IContact){
    if(contact?.street != null && contact?.zip != null && contact?.city != null){
      return true
    }
    return false
  }

  // delete confirmation dialog
  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }
}
